/**
 * TodoBadgeSystem.js
 * 모바일 To-Do 리스트용 간단한 배지 시스템
 * 지속적인 활용과 활동에 대한 배지 제공
 */

import React, { useState, useEffect } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const TodoBadgeSystem = () => {
  const [badges, setBadges] = useState([]);

  // 통계 조회
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // 오늘 완료한 To-Do 수
      const today = new Date().toISOString().split('T')[0];
      const todosResponse = await axios.get(`${API_BASE_URL}/todos`);
      const allTodos = todosResponse.data || [];
      
      const todayCompleted = allTodos.filter(t => 
        t.status === 'completed' && 
        t.updated_at && 
        t.updated_at.startsWith(today)
      ).length;

      // 연속 완료 일수 계산 (간단한 버전)
      const completedDates = allTodos
        .filter(t => t.status === 'completed' && t.updated_at)
        .map(t => t.updated_at.split('T')[0])
        .filter((date, index, self) => self.indexOf(date) === index)
        .sort()
        .reverse();

      let streakDays = 0;
      let checkDate = new Date(today);
      
      for (let i = 0; i < 30; i++) {
        const dateStr = checkDate.toISOString().split('T')[0];
        if (completedDates.includes(dateStr)) {
          streakDays++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      }

      // 이번 주 활동 (To-Do 추가/완료)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weeklyActivity = allTodos.filter(t => 
        (t.created_at && new Date(t.created_at) >= weekAgo) ||
        (t.updated_at && new Date(t.updated_at) >= weekAgo)
      ).length;

      // 배지 계산
      const earnedBadges = [];
      
      // 스트릭 배지
      if (streakDays >= 7) {
        earnedBadges.push({ id: 'streak_7', label: '7일 연속', color: '#FF6B6B' });
      }
      if (streakDays >= 14) {
        earnedBadges.push({ id: 'streak_14', label: '14일 연속', color: '#4ECDC4' });
      }
      if (streakDays >= 30) {
        earnedBadges.push({ id: 'streak_30', label: '30일 연속', color: '#45B7D1' });
      }

      // 활동 배지
      if (weeklyActivity >= 10) {
        earnedBadges.push({ id: 'weekly_10', label: '이번 주 활발', color: '#96CEB4' });
      }
      if (weeklyActivity >= 20) {
        earnedBadges.push({ id: 'weekly_20', label: '이번 주 매우 활발', color: '#FFEAA7' });
      }

      // 완료 배지
      if (todayCompleted >= 5) {
        earnedBadges.push({ id: 'today_5', label: '오늘 5개 완료', color: '#DDA15E' });
      }

      setBadges(earnedBadges);
    } catch (error) {
      console.error('통계 조회 실패:', error);
    }
  };

  if (badges.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 3, pb: 1.5 }}>
      {badges.map((badge) => (
        <Tooltip key={badge.id} title={`지속적인 활용: ${badge.label}`} arrow>
          <Chip
            label={badge.label}
            size="small"
            sx={{
              backgroundColor: badge.color,
              color: '#FFFFFF',
              fontSize: '10px',
              height: '22px',
              fontWeight: 600,
              '&:hover': {
                opacity: 0.9,
              },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export default TodoBadgeSystem;

